import {
  Box,
  ClickAwayListener,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { throttle } from 'lodash';
import {
  Children,
  cloneElement,
  CSSProperties,
  FC,
  Fragment,
  HTMLAttributes,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WbNoData } from '../WbNoData';
import { WbPopover } from '../WbPopover';
import { WbSearch } from '../WbSearch';
import { getOnHoverScrollbarStyle } from '../../styles';

const useStyles = makeStyles(
  theme => ({
    root: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      '& :nth-last-child(1)': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
    },
    hasHiddenCount: {
      '& > :nth-last-child(1)': {
        overflow: 'visible !important',
        textOverflow: 'clip',
      },

      '& :nth-last-child(2)': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
    },
    tooltip: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    fullList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      alignItems: 'flex-start',
      flex: 1,
      '& > *': { flexShrink: 0 },
      overflowX: 'hidden',
      overflowY: 'auto',
      ...getOnHoverScrollbarStyle(theme, false),
    },
    hiddenCount: {
      borderRadius: theme.spacing(0.5),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      ...theme.typography.body2,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      backgroundColor: theme.palette.softened?.primary,
      color: theme.palette.secondary?.dark,
    },
    popoverContainer: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      maxHeight: '70vh',
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    },
    measureLayer: {
      position: 'absolute',
      visibility: 'hidden',
      display: 'flex',
      top: '0px',
      left: '0px',
    },
    wrapper: {
      display: 'flex',
      height: '100%',
      alignItems: 'center',
      width: '100%',
      position: 'relative',
    },
    icon: {
      fontSize: 'small',
      height: '16px',
      width: '16px',
    },
  }),
  { name: 'WbComponentsListArray' },
);

const MIN_SPACE_TO_ADD_ELEMENT = 30;
const COUNTER_SPACE_ESTIMATION = 18;

interface WbComponentsListArrayProps {
  children: ReactElement[];
  limit?: number;
  className?: string;
  filterOnChange?: (child: ReactNode, query: string) => boolean;
  style?: CSSProperties;
  popoverContent?: ReactNode;
  showLegend?: boolean;
  iconLegendButton?: ReactElement;
  gap?: number;
  renderContainer?: (
    children: ReactNode,
    context: { totalWidth: number | null },
  ) => JSX.Element;
}
interface ExceedLinkProps {
  hiddenCount: number;
  childrenArray: ReactNode[];
  filterOnChange?: (child: ReactNode, query: string) => boolean;
  popoverContent?: ReactNode;
  iconLegendButton?: ReactElement;
}

const ExceedLink: FC<ExceedLinkProps> = ({
  hiddenCount,
  childrenArray,
  filterOnChange,
  popoverContent,
  iconLegendButton,
}) => {
  const classes = useStyles();
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');

  const filteredChildren = useMemo(() => {
    return childrenArray.filter(child => {
      if (!isValidElement(child)) return false;

      return filterOnChange ? filterOnChange(child, query) : true;
    });
  }, [childrenArray, query, filterOnChange]);

  const onClickHandler = (e: MouseEvent) => {
    e.preventDefault();
    setShowAll(true);
  };

  return (
    <ClickAwayListener onClickAway={() => setShowAll(false)}>
      <div>
        <WbPopover
          open={showAll}
          placement="bottom-end"
          onClose={() => setShowAll(false)}
          interactive
          disableHoverListener
          PopperProps={{
            style: {
              maxWidth: '400px',
            },
          }}
          component={
            popoverContent ?? (
              <Box className={classes.popoverContainer}>
                {filterOnChange && (
                  <WbSearch
                    value={query}
                    debounced
                    onChange={setQuery}
                    style={{ marginBottom: '8px' }}
                  />
                )}
                <div className={classes.fullList}>
                  {filteredChildren.length ? (
                    filteredChildren.map((child, index) => {
                      if (!isValidElement(child)) {
                        return <Fragment key={index}>{child}</Fragment>;
                      }

                      return cloneElement(child, {
                        key: child.key ?? index,
                        style: {
                          wordBreak: 'break-all',
                          whiteSpace: 'wrap',
                          height: 'auto',
                          maxWidth: '100%',
                          overflow: 'hidden',
                        },
                      } as HTMLAttributes<HTMLElement>);
                    })
                  ) : (
                    <WbNoData />
                  )}
                </div>
              </Box>
            )
          }
        >
          {iconLegendButton && !hiddenCount ? (
            <IconButton
              onClick={onClickHandler}
              block-row-click="true"
              style={{ padding: '4px' }}
              color="primary"
              aria-label="show legend"
            >
              {cloneElement(iconLegendButton, {
                className: classes.icon,
              })}
            </IconButton>
          ) : (
            <Link block-row-click="true" onClick={onClickHandler}>
              <Typography
                variant="body1"
                style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                +{hiddenCount}
              </Typography>
            </Link>
          )}
        </WbPopover>
      </div>
    </ClickAwayListener>
  );
};

/**
 * WbComponentsListArray component renders a list of children with a specified limit.
 * It dynamically calculates how many children can be displayed based on available width.
 */
export const WbComponentsListArray: FC<WbComponentsListArrayProps> = ({
  children,
  limit,
  className,
  filterOnChange,
  style,
  popoverContent,
  showLegend = false,
  iconLegendButton,
  renderContainer,
  gap = 4,
}) => {
  const classes = useStyles();
  const childrenArray = Children.toArray(children);

  const [fitCount, setFitCount] = useState<number | null>(null);
  const measureLayerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const [totalWidth, setTotalWidth] = useState<number | null>(null);

  // Observe container width changes and update state
  useEffect(() => {
    const handleResize = throttle(entries => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setContainerWidth(width);
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Calculate how many children can fit within the container width
  useLayoutEffect(() => {
    if (!measureLayerRef.current || !containerWidth || !childrenArray.length)
      return;

    const elements = measureLayerRef.current.children;
    if (elements.length === 0) return;
    const counterSpace = gap + COUNTER_SPACE_ESTIMATION;
    let remaining = containerWidth;
    let count = 0;
    let truncated = false;

    let tmpTotal = 0;
    for (let i = 0; i < elements.length; i++) {
      const elWidth = Math.ceil(elements[i].getBoundingClientRect().width);
      const gapIncluded = elWidth + (i > 0 ? gap : 0);
      tmpTotal += gapIncluded;
    }

    // we only need to reserve space for the counter if we know not all elements fit in the space
    if (tmpTotal > containerWidth) remaining -= counterSpace;

    for (let i = 0; i < elements.length; i++) {
      const elWidth = Math.ceil(elements[i].getBoundingClientRect().width);
      const subtract = elWidth + (i > 0 ? gap : 0);

      // to include the next element, there must be atleast MIN_SPACE_TO_ADD_ELEMENT for the element itself (excluding the gap)
      if (remaining <= MIN_SPACE_TO_ADD_ELEMENT + gap) continue;

      if (remaining - subtract < 0) {
        truncated = true;
      } else {
        truncated = false;
      }

      remaining -= subtract;
      count++;
    }

    setIsTruncated(truncated);
    setFitCount(count);
    setTotalWidth(tmpTotal);
  }, [childrenArray, containerWidth, gap]);

  // Determine number of children to display based on limit and fit count
  const displayCount = useMemo(() => {
    if (typeof limit === 'number' && typeof fitCount === 'number') {
      return Math.min(limit, fitCount);
    }

    if (typeof fitCount === 'number') return fitCount;
    if (typeof limit === 'number') return limit;

    return childrenArray.length;
  }, [limit, fitCount, childrenArray.length]);

  // Get the visible children that can be displayed
  const visibleChildren = useMemo(() => {
    return childrenArray.slice(0, displayCount);
  }, [childrenArray, displayCount]);

  const hiddenCount = childrenArray.length - visibleChildren.length;

  const childrenToMeasure = useMemo(() => {
    if (limit) return childrenArray.slice(0, limit);
    return childrenArray;
  }, [childrenArray, limit]);

  const content = (
    <div className={classes.wrapper} ref={containerRef}>
      <Box
        className={clsx(
          className,
          classes.root,
          hiddenCount > 0 && classes.hasHiddenCount,
        )}
        style={{ overflow: 'hidden', flexWrap: 'nowrap', gap, ...style }}
      >
        {visibleChildren.map((child, index) => {
          if (!isValidElement(child)) {
            return <Fragment key={index}>{child}</Fragment>;
          }

          const element = cloneElement(child, {
            key: child.key ?? index,
          } as HTMLAttributes<HTMLElement>);

          return index === visibleChildren.length - 1 && isTruncated ? (
            <WbPopover
              placement="top"
              key={child.key ?? index}
              component={element}
              className={classes.tooltip}
            >
              <div
                style={{
                  display: 'inline-flex',
                }}
              >
                {element}
              </div>
            </WbPopover>
          ) : (
            element
          );
        })}
        {(hiddenCount > 0 || showLegend) && (
          <ExceedLink
            hiddenCount={hiddenCount}
            childrenArray={childrenArray}
            filterOnChange={filterOnChange}
            popoverContent={popoverContent}
            iconLegendButton={iconLegendButton}
          />
        )}
      </Box>
      {/* Render all children in an invisible div so they can be measured */}
      <div
        className={classes.measureLayer}
        ref={measureLayerRef}
        aria-hidden
        style={{ gap }}
      >
        {childrenToMeasure.map((child, index) => (
          <Fragment key={isValidElement(child) ? child.key ?? index : index}>
            {child}
          </Fragment>
        ))}
      </div>
    </div>
  );

  return renderContainer ? renderContainer(content, { totalWidth }) : content;
};
